import React, { useState, useEffect } from "react";
import { FaMobile, FaSync, FaExclamationCircle, FaPlus, FaMinus, FaRandom, FaCog, FaInfoCircle } from "react-icons/fa";
import { EmptyState, MascotLoadingOverlay } from "../../../shared";
import { FirmDropdown } from "../../../atoms";
import { Alert, Modal, Backdrop, Fade, Box } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import useAuth from "../../../../Hooks/useAuth";
import { useDebounce } from "../../../../Hooks/useDebounce";
import useFirmScopeSelection from "../../../../Hooks/useFirmScopeSelection";
import { distributeResultAlert } from "../../../../utils/quotaDistributeResult";
import usePermission from "../../../../Hooks/usePermission";
import {
  useLazyGetDeviceByFirmIDQuery,
  useLazyGetSyncByFirmIDQuery,
  useLazyGetFirmProductsQuery,
  useUpdateGiveFirmProductQuotaMutation,
  useUpdateTakeBackFirmProductQuotaMutation,
} from "../../../../store/storageRoles/firmApi";
import useFirmQuotaDistribution from "../../../../Hooks/storageRoles/useFirmQuotaDistribution";
import {
  useToggleDeviceActivityMutation,
  useMoveDeviceToPlaymakerMutation,
  useMoveDeviceToServiceMutation,
} from "../../../../store/storageRoles/devicesApi";
import {
  useUpdateDeviceSyncSettingMutation,
} from "../../../../store/storageRoles/devicesSharedApi";
import { useFetchPlayMakerQuery } from "../../../../store/storageRoles/usersApi";

// Import bileşenleri
import DevicesTableSkeleton from "../DevicesTableSkeleton";
import DevicesTable from "../DevicesTable";
import DeviceOperationsDrawer from "../DeviceOperationsDrawer";
import QuotaOperationDrawer from "../QuotaOperationDrawer";
import ManualQuotaDrawer from "../ManualQuotaDrawer";
import DeviceSettingsDrawer from "../DeviceSettingsDrawer";
import OperationDrawer from "../../../Device/OperationDrawer";
import CenterDealerMoveDrawer from "../../../Device/storageRoles/CenterDealerMoveDrawer";
import CenterDealerQuotaDrawer from "../../../Device/storageRoles/CenterDealerQuotaDrawer";
import { DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE, DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN } from "../../../../utils/deviceActivityLabels";

const DevicesTab = ({ firm, readOnly = false }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const [filterSerialNo, setFilterSerialNo] = useState("");
  const [filterEyeCount, setFilterEyeCount] = useState("");
  const debouncedSerialNo = useDebounce(filterSerialNo, 250);
  const debouncedEyeCount = useDebounce(filterEyeCount, 250);
  // Alt firma süzgeci — VARSAYILAN: firma + TÜM alt firmaları (kullanıcı
  // kararı 2026-09-14). Sunucu zaten ağacın tamamını döndürüyor; daraltan
  // taraf BURASIYDI, dokunulmamış seçimde listeyi kök firmaya süzüyordu.
  // Ayrıntı: hooks/useFirmScopeSelection.
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [quotaData, setQuotaData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [devices, setDevices] = useState([]);
  // Merkezden bayi adına taşıma (#329) — kapanış kayması boyunca cihaz/mod
  // korunur, yalnız open düşer.
  const [centerDealerMove, setCenterDealerMove] = useState({ open: false });
  // Bayi cihazına Kota Ver / Kota Al (#335) — aynı kapanış kuralı
  const [centerDealerQuota, setCenterDealerQuota] = useState({ open: false });

  // OperationDrawer state'leri
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDevice, setDrawerDevice] = useState(null);
  const [drawerOperation, setDrawerOperation] = useState(null);

  // Kota dağıtımı toggle state
  const [showQuotaToggleModal, setShowQuotaToggleModal] = useState(false);
  const [quotaToggleDevice, setQuotaToggleDevice] = useState(null);
  const [quotaToggleProduct, setQuotaToggleProduct] = useState(null);

  // Kota dağıt onay modal state
  const [showDistributeConfirm, setShowDistributeConfirm] = useState(false);

  // Bedelsiz kota / iade state
  const [quotaModal, setQuotaModal] = useState({ open: false, type: "" });

  // Manuel kota drawer (cihaz kartı 3 nokta menüsünden)
  const [manualQuotaDrawer, setManualQuotaDrawer] = useState({ open: false, device: null });

  // Ayarlar drawer (cihaz kartı 3 nokta menüsünden — Genel Ayarlar / Göz Ayarları)
  const [settingsDrawer, setSettingsDrawer] = useState({ open: false, device: null });

  // Operasyonlar drawer'ı — tablo satırındaki buton açar; tüm aksiyon
  // ve detay görünümleriyle çalışır. deviceId
  // saklanır ki refresh sonrası güncel cihaz objesi drawer'a yansısın.
  const [operationsDrawer, setOperationsDrawer] = useState({ open: false, deviceId: null, view: null });

  const [
    getDeviceByFirmID,
    {
      data: devicesData,
      isLoading: deviceIsLoading,
      isFetching: deviceIsFetching,
      error: deviceError,
      isError: deviceIsError,
    },
  ] = useLazyGetDeviceByFirmIDQuery();

  // Kota dağıt — firma bazında tek dağıtım; cihaz listesindeki "Kota Dağıt" ile
  // aynı kayıt (Hooks/storageRoles/useFirmQuotaDistribution). Sürerken bu
  // firmanın işlemleri her yerde meşgul görünür.
  const { isBusy: isFirmQuotaBusy, distributeScope: distributeScopeQuota } = useFirmQuotaDistribution();
  const isDistributingQuota = isFirmQuotaBusy(firm?._id);

  // Cihaz işlemleri için mutations
  const [moveDeviceToService, serviceMoveStatus] =
    useMoveDeviceToServiceMutation();
  const [moveDeviceToPlaymaker, playmakerMoveStatus] =
    useMoveDeviceToPlaymakerMutation();

  const [toggleDeviceActivity, resultToggleDeviceActivity] =
    useToggleDeviceActivityMutation();

  // Cihaz sync ayarları (quotaDistribution toggle)
  const [updateDeviceSetting, updateDeviceSettingResult] = useUpdateDeviceSyncSettingMutation();

  // Firma ürün kota bilgisi
  const [getProducts, { data: productsResponse, isLoading: productsLoading }] =
    useLazyGetFirmProductsQuery();

  // Backend'den dönen güncel kota/cihaz verisi (give/takeback/distribute sonrası)
  const [syncDataFromResponse, setSyncDataFromResponse] = useState(null);

  // Bedelsiz kota / iade mutations
  const [giveQuota, { data: giveData, isLoading: giveLoading, isSuccess: giveSuccess, isError: giveIsError, error: giveError, reset: resetGive }] =
    useUpdateGiveFirmProductQuotaMutation();
  const [takebackQuota, { data: takebackData, isLoading: takebackLoading, isSuccess: takebackSuccess, isError: takebackIsError, error: takebackError, reset: resetTakeback }] =
    useUpdateTakeBackFirmProductQuotaMutation();

  // Plasiyer listesi — readOnly modda (dealer rolleri) operasyon butonları kapalı olduğu için istek atılmıyor
  // Uç users.read ister — izni yoksa istek atılmaz (#414)
  const { data: playmakerData } = useFetchPlayMakerQuery(auth?.accessToken, {
    skip: readOnly || !hasPermission("users.read"),
  });

  const [getSyncByFirmID, { data: syncDataResponse }] = useLazyGetSyncByFirmIDQuery();

  useEffect(() => {
    if (syncDataResponse?.data) {
      setQuotaData(syncDataResponse.data.quotaArray || []);
      setSyncDataFromResponse(null);
    }
  }, [syncDataResponse?.data]);

  useEffect(() => {
    if (firm?._id && auth?.accessToken) {
      getDeviceByFirmID({
        id: firm._id,
        token: auth.accessToken,
      });

      getSyncByFirmID({
        id: firm._id,
        token: auth.accessToken,
      });

      getProducts({
        id: firm._id,
        token: auth.accessToken,
      });
    }
  }, []);

  // Drawer'ı sadece butonla açılsın: firma değişince veya bu sekme (mount) açılınca kapalı olsun
  useEffect(() => {
    setQuotaModal({ open: false, type: "" });
  }, [firm?._id]);

  useEffect(() => {
    setQuotaModal({ open: false, type: "" });
  }, []);

  useEffect(() => {
    if (devicesData?.data) {
      setDevices(devicesData.data.devices);
    }
  }, [devicesData?.data]);

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(null);
      }, 5000);
    }
  }, [alert]);

  // Yardımcı: işlem sonrası cihaz listesini yenile
  const refreshAllData = async () => {
    try {
      const result = await getDeviceByFirmID({ id: firm._id, token: auth.accessToken }).unwrap();
      if (result?.data?.devices) {
        setDevices(result.data.devices);
      }
    } catch (e) {
      // Hata durumunda sessiz kal
    }
    getSyncByFirmID({ id: firm._id, token: auth.accessToken });
    getProducts({ id: firm._id, token: auth.accessToken });
  };


  // Servise alma sonucu
  useEffect(() => {
    if (serviceMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla servise alındı." });
      refreshAllData();
    }
    if (serviceMoveStatus.isError) {
      setAlert({ severity: "error", message: serviceMoveStatus.error?.data?.message || "Servise alırken bir hata oluştu." });
    }
  }, [serviceMoveStatus.isSuccess, serviceMoveStatus.isError]);

  // Cihazı kota almaya açma/kapama sonucu
  useEffect(() => {
    if (resultToggleDeviceActivity.isSuccess) {
      setAlert({ severity: "success", message: "Kota alma durumu güncellendi." });
      refreshAllData();
    }
    if (resultToggleDeviceActivity.isError) {
      setAlert({ severity: "error", message: resultToggleDeviceActivity.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
  }, [resultToggleDeviceActivity.isSuccess, resultToggleDeviceActivity.isError]);

  // Plasiyere taşıma sonucu
  useEffect(() => {
    if (playmakerMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla plasiyere verildi." });
      refreshAllData();
    }
    if (playmakerMoveStatus.isError) {
      setAlert({ severity: "error", message: playmakerMoveStatus.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
  }, [playmakerMoveStatus.isSuccess, playmakerMoveStatus.isError]);

  // Bedelsiz kota yükleme sonucu: güncel veri için ayrı istekle yenile (backend aynı response'ta eski veri dönebiliyor)
  useEffect(() => {
    if (giveSuccess) {
      setAlert({ severity: "success", message: "Kota başarıyla yüklendi." });
      setQuotaModal({ open: false, type: "" });
      setSyncDataFromResponse(null);
      refreshAllData();
      resetGive();
    }
    if (giveIsError) {
      setAlert({ severity: "error", message: giveError?.data?.message || "Kota yüklenirken bir hata oluştu." });
      setQuotaModal({ open: false, type: "" });
      resetGive();
    }
  }, [giveSuccess, giveIsError]);

  // İade sonucu: güncel veri için ayrı istekle yenile (backend aynı response'ta eski veri dönebiliyor)
  useEffect(() => {
    if (takebackSuccess) {
      setAlert({ severity: "success", message: "Kota başarıyla iade alındı." });
      setQuotaModal({ open: false, type: "" });
      setSyncDataFromResponse(null);
      refreshAllData();
      resetTakeback();
    }
    if (takebackIsError) {
      setAlert({ severity: "error", message: takebackError?.data?.message || "Kota iade alınırken bir hata oluştu." });
      setQuotaModal({ open: false, type: "" });
      resetTakeback();
    }
  }, [takebackSuccess, takebackIsError]);

  // Cihaz quota distribution toggle sonucu
  useEffect(() => {
    if (updateDeviceSettingResult.isSuccess) {
      setAlert({ severity: "success", message: "Kota alma durumu güncellendi." });
      setShowQuotaToggleModal(false);
      refreshAllData();
    }
    if (updateDeviceSettingResult.isError) {
      setAlert({ severity: "error", message: updateDeviceSettingResult.error?.data?.message || "Ayar güncellenirken hata oluştu." });
      setShowQuotaToggleModal(false);
    }
  }, [updateDeviceSettingResult.isSuccess, updateDeviceSettingResult.isError]);

  const handleRefresh = () => {
    setSyncDataFromResponse(null);
    if (firm?._id && auth?.accessToken) {
      getDeviceByFirmID({ id: firm._id, token: auth.accessToken });
      getSyncByFirmID({ id: firm._id, token: auth.accessToken });
      getProducts({ id: firm._id, token: auth.accessToken });
    }
  };

  // Kota dağıt — confirm modal aç
  const handleDistributeQuota = () => {
    if (!firm?.firmName || isDistributingQuota) return;
    setShowDistributeConfirm(true);
  };

  // Kota dağıt — onaylandı
  // Backend payload dönmüyor; güncel veri için ayrı istekle yenilenir.
  const handleDistributeConfirm = async () => {
    setShowDistributeConfirm(false);
    try {
      // Kapsamlı dağıtım (#386): seçicideki her firma kendi havuzunu kendi
      // cihazlarına; alt firması olmayan firmada yalnız kök.
      const result = await distributeScopeQuota(
        firm,
        hasSubFirms ? effectiveFirmIds : [],
        distributeInfo.scopeFirms.map((f) => f._id)
      );
      if (!result) return;
      setAlert(distributeResultAlert(result));
      setSyncDataFromResponse(null);
      refreshAllData();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Kota dağıtımı sırasında bir hata oluştu." });
    }
  };


  // OperationDrawer handler fonksiyonları
  const handleOpenOperationDrawer = (device, operation) => {
    setDrawerDevice(device);
    setDrawerOperation(operation);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerDevice(null);
    setDrawerOperation(null);
  };

  const handleOpenManualQuota = (device) => {
    setManualQuotaDrawer({ open: true, device });
  };

  const handleManualQuotaSuccess = () => {
    // Drawer "Tamamlandı"ya kadar açık kalır; arka planı tazele
    setAlert({ severity: "success", message: "Manuel kota güncellendi." });
    getDeviceByFirmID({ id: firm._id, token: auth.accessToken });
  };

  const handleOpenSettingsDrawer = (device) => {
    setSettingsDrawer({ open: true, device });
  };

  const handleDrawerConfirm = (operationData) => {
    const { device, connect, description, selectedPlaymakerId: playmakerId } = operationData;
    const token = auth.accessToken;

    if (drawerOperation === "Servis") {
      moveDeviceToService({ id: device._id, connect, description, token, isActive: operationData.isActive ?? true });
    } else if (drawerOperation === "AktiflikKapat") {
      toggleDeviceActivity({
        token,
        id: device._id,
        connect,
        description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE,
        isActive: false,
      });
    } else if (drawerOperation === "AktiflikAc") {
      toggleDeviceActivity({
        token,
        id: device._id,
        connect,
        description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN,
        isActive: true,
      });
    } else if (drawerOperation === "Plasiyer") {
      if (!playmakerId) return;
      moveDeviceToPlaymaker({
        token,
        id: device._id,
        connect,
        userID: playmakerId,
        description: description || "Cihaz plasiyere verildi.",
      });
    }
  };

  // Kota dağıtımı toggle handler
  const handleToggleQuotaDistribution = (device, product) => {
    setQuotaToggleDevice(device);
    setQuotaToggleProduct(product);
    setShowQuotaToggleModal(true);
  };

  const handleQuotaToggleConfirm = () => {
    if (!quotaToggleDevice || !quotaToggleProduct) return;
    const productInfo = quotaToggleDevice.productInfo?.productInfo?.find(
      (p) => p.productName === quotaToggleProduct.productName
    );
    if (!productInfo) return;

    updateDeviceSetting({
      id: quotaToggleDevice._id,
      productName: quotaToggleProduct.productName,
      quotaWarning: productInfo.quotaWarning,
      quotaMax: productInfo.quotaMax,
      syncLevel: productInfo.syncLevel,
      quotaDistribution: !quotaToggleProduct.quotaDistribution,
      token: auth.accessToken,
    });
  };

  // Bedelsiz kota / iade handler — drawer cevap gelene kadar açık kalır, loading overlay gösterilir
  const handleQuotaSubmit = (formData) => {
    const mutation = quotaModal.type === "upload" ? giveQuota : takebackQuota;
    mutation({ ...formData, note: formData.note || "", token: auth.accessToken, id: firm._id });
  };

  const productDatas = productsResponse?.data || [];

  // Kartlar: give/takeback/distribute sonrası backend'in döndürdüğü güncel veriyi kullan; yoksa getFirmProducts + quotaData
  //
  // KOTA DAGITIMI KAPALI URUN GIZLI (2026-08-28 kullanici karari).
  // `getFirmProducts` bunu SUNUCUDA filtreliyor; ama kota islemi sonrasi
  // donen `syncDataFromResponse.productInfoes` HAM listedir — burada da
  // elemek gerekiyor, yoksa islem sonrasi kapali urun kartlarda bir anda
  // geri geliyordu.
  const baseProductsForCards = (() => {
    const fromResponse = syncDataFromResponse?.productInfoes?.filter(
      (p) => p.quotaDistribution !== false,
    );
    const responseQuotaArray = syncDataFromResponse?.quotaArray;
    if (Array.isArray(fromResponse) && fromResponse.length > 0) {
      return fromResponse.map((p) => {
        const deviceQuota = responseQuotaArray?.find((q) => q.name === p.productName)?.quota ?? 0;
        const firmQuota = p.quota ?? 0;
        return {
          productName: p.productName,
          _id: p._id,
          totalQuota: firmQuota + deviceQuota,
          deviceQuota,
          undistributedQuota: firmQuota,
        };
      });
    }
    return productDatas.map((p) => {
      const totalQuota = p.quota ?? 0;
      const deviceQuota = quotaData.find((q) => q.name === p.productName)?.quota ?? 0;
      return {
        productName: p.productName,
        _id: p._id,
        totalQuota,
        deviceQuota,
        undistributedQuota: totalQuota - deviceQuota,
      };
    });
  })();

  const quotaActionLoading = giveLoading || takebackLoading || isDistributingQuota;

  // Seri no ve göz sayısına göre filtreleme (header input'ları)
  // Listedeki cihazların ait olduğu firmalar — alt firma cihazları da geldiği
  // için birden fazla olabilir. Ana firma her zaman ilk sırada.
  const firmOptions = (() => {
    const map = new Map();
    // firmType seçicide tip ikonunu belirler — kök bayi ise liste bayinin
    // müşterilerini de içerir (#200).
    if (firm?._id) {
      map.set(String(firm._id), {
        _id: String(firm._id),
        firmName: firm.firmName,
        firmType: firm.firmType,
      });
    }
    devices.forEach((d) => {
      const id = String(d.firm?._id || d.firm || "");
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          _id: id,
          firmName: d.firm?.firmName || "-",
          firmType: d.firm?.firmType,
        });
      }
    });
    return [...map.values()];
  })();
  const {
    requestFirmIds: effectiveFirmIds,
    selectedFirms,
    onChange: onScopeChange,
    hasSubFirms,
  } = useFirmScopeSelection(firmOptions);

  // Kota kartları firma seçicinin KAPSAMINI izler (#300). Yukarıdaki taban
  // değerler yalnız kök firmanındır (havuz + kendi cihazları); seçili ALT
  // firmaların cihazlarındaki kota Toplam'a eklenir. Kök firma seçili değilse
  // Toplam yalnız seçili alt firma cihazlarıdır. "Dağıtılmayan" her zaman kök
  // firmanın havuzudur — kota ver/al oradan yapılır. Seri no / göz filtreleri
  // kartı etkilemez. `effectiveFirmIds` boş = tümü (useFirmScopeSelection).
  const rootFirmId = firm?._id ? String(firm._id) : "";
  const deviceFirmId = (d) => String(d.firm?._id || d.firm || "");
  const inScope = (id) => effectiveFirmIds.length === 0 || effectiveFirmIds.includes(id);
  const subScopeDevices = hasSubFirms
    ? devices.filter((d) => deviceFirmId(d) !== rootFirmId && inScope(deviceFirmId(d)))
    : [];
  const rootInScope = !hasSubFirms || inScope(rootFirmId);
  const productsForCards = baseProductsForCards.map((p) => {
    const subDeviceQuota = subScopeDevices.reduce((sum, d) => {
      const item = d.productInfo?.productInfo?.find((i) => i.productName === p.productName);
      return sum + (Number(item?.quota) || 0);
    }, 0);
    return {
      ...p,
      totalQuota: (rootInScope ? p.totalQuota : 0) + subDeviceQuota,
    };
  });

  // Kota dağıt bilgi hesaplaması — firma seçicisinin kapsamı (#386). Dağıtım
  // her firmada kendi havuzundan kendi cihazlarına yapılır.
  const distributeInfo = (() => {
    const scopeDevices = hasSubFirms ? devices.filter((d) => inScope(deviceFirmId(d))) : devices;
    const activeDevices = scopeDevices.filter((d) => d.isActive);
    const quotaEnabledDevices = activeDevices.filter((d) =>
      d.productInfo?.productInfo?.some((p) => p.quotaDistribution)
    );
    const totalDeviceQuota = scopeDevices.reduce((sum, d) => {
      return sum + (d.productInfo?.productInfo || []).reduce((s, p) => s + (p.quota || 0), 0);
    }, 0);
    const scopeFirms = hasSubFirms ? firmOptions.filter((f) => inScope(String(f._id))) : firmOptions.slice(0, 1);
    return {
      totalDevices: scopeDevices.length,
      activeDevices: activeDevices.length,
      quotaEnabledDevices: quotaEnabledDevices.length,
      totalDeviceQuota,
      scopeFirms,
    };
  })();

  const displayDevices = (() => {
    let list = isSearching ? filteredDevices : devices;
    // Süzgeçler gecikmeli (#407): her tuşta tüm kartlar yeniden çizilmesin.
    const sn = (debouncedSerialNo || "").trim().toLowerCase();
    const eye = (debouncedEyeCount || "").trim();
    const firmFilterActive = hasSubFirms && effectiveFirmIds.length > 0;
    if (!sn && !eye && !firmFilterActive) return list;
    return list.filter((d) => {
      if (firmFilterActive) {
        const id = String(d.firm?._id || d.firm || "");
        if (!effectiveFirmIds.includes(id)) return false;
      }
      if (sn && !(d.serialNo || "").toLowerCase().includes(sn)) return false;
      if (eye) {
        const num = parseInt(eye, 10);
        const cup = d.deviceInfo?.cupNumber ?? d.cupNumber;
        if (!Number.isNaN(num)) {
          if (cup === undefined || cup === null || Number(cup) !== num) return false;
        } else if (!String(cup ?? "").includes(eye)) return false;
      }
      return true;
    });
  })();
  const hasNoData =
    !deviceIsLoading && !deviceIsFetching && displayDevices.length === 0;

  // Drawer'daki cihaz — her zaman güncel listeden çözülür (refresh sonrası
  // bayat obje kalmasın)
  const operationsDrawerDevice =
    devices.find((d) => d._id === operationsDrawer.deviceId) || null;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Alert */}
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              width: "100%",
              borderRadius: "12px",
              fontSize: "13px",
            }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shrink-0 px-2 sm:px-4 py-3 sm:py-4 space-y-2.5">
        {/* Row 1: Title + Search Inputs (sağa dayalı) */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-lg shrink-0">
              <FaMobile className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-800 leading-tight">
                Cihaz Yönetimi
              </h2>
              <p className="text-[11px] sm:text-xs text-gray-500">
                {displayDevices.length} cihaz
                {(filterSerialNo || filterEyeCount || effectiveFirmIds.length !== firmOptions.length) &&
                  ` (${devices.length} toplam)`}
              </p>
            </div>
          </div>

          {/* Seri No ve Göz Sayısı filtreleri — sağa dayalı */}
          <div className="flex items-center gap-2.5 ml-auto">
            {/* Alt firma süzgeci — yalnızca alt firma cihazı varsa. Çoklu
                seçim; varsayılan "tümü" (hiç dokunulmamış hali). */}
            {hasSubFirms && (
              <div className="w-44 sm:w-56">
                <FirmDropdown
                  compact
                  multiple
                  firms={firmOptions}
                  value={selectedFirms}
                  placeholder="Firma seçin"
                  onChange={onScopeChange}
                />
              </div>
            )}
            <input
              type="text"
              placeholder="Seri No"
              value={filterSerialNo}
              onChange={(e) => setFilterSerialNo(e.target.value)}
              className="w-36 sm:w-44 md:w-52 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="Göz sayısı"
              value={filterEyeCount}
              onChange={(e) => setFilterEyeCount(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="w-20 sm:w-24 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Row 2: Ürün Kota Kartları (sol) + Action Buttons (sağ) — aynı satır */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Ürün Kota Bilgisi — ilk yüklemede (henüz veri yokken) de
              skeleton görünsün */}
          {(productsLoading || productsForCards?.length > 0) && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0 -mx-1 px-1">
              {!syncDataFromResponse && productsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="shrink-0 flex items-center gap-2.5 bg-gray-50 rounded-xl px-4 py-2 animate-pulse">
                    <div className="w-1.5 h-10 bg-gray-200 rounded-full" />
                    <div>
                      <div className="h-3 w-16 bg-gray-200 rounded mb-1.5" />
                      <div className="h-5 w-20 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))
              ) : (
                productsForCards.map((product, index) => {
                  const quotaColors = [
                    { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", label: "text-blue-500", dot: "bg-blue-400", undist: "text-blue-400" },
                    { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "text-emerald-500", dot: "bg-emerald-400", undist: "text-emerald-400" },
                    { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", label: "text-amber-500", dot: "bg-amber-400", undist: "text-amber-400" },
                    { bg: "bg-indigo-50 border-indigo-200", text: "text-indigo-700", label: "text-indigo-500", dot: "bg-indigo-400", undist: "text-indigo-400" },
                    { bg: "bg-pink-50 border-pink-200", text: "text-pink-700", label: "text-pink-500", dot: "bg-pink-400", undist: "text-pink-400" },
                    { bg: "bg-purple-50 border-purple-200", text: "text-purple-700", label: "text-purple-500", dot: "bg-purple-400", undist: "text-purple-400" },
                  ];
                  const color = quotaColors[index % quotaColors.length];
                  const totalQuota = product.totalQuota ?? 0;
                  const undistributedQuota = product.undistributedQuota ?? 0;

                  return (
                    <div key={product._id || product.productName || index} className={`shrink-0 ${color.bg} border rounded-xl px-4 py-2.5 transition-all hover:shadow-sm`}>
                      <p className={`text-xs font-bold ${color.label} whitespace-nowrap leading-none mb-1.5`}>{product.productName}</p>
                      <div className="flex items-baseline gap-2">
                        <div className="text-center">
                          <p className={`text-lg font-Bold ${color.text} leading-none tabular-nums`}>{totalQuota.toLocaleString("tr-TR")}</p>
                          <p className="text-[9px] font-medium text-gray-400 mt-0.5">Toplam Kota</p>
                        </div>
                        <span className="text-gray-300 font-light text-lg leading-none">|</span>
                        <div className="text-center">
                          <p className={`text-lg font-Bold ${undistributedQuota < 0 ? "text-red-500" : color.undist} leading-none tabular-nums`}>{undistributedQuota.toLocaleString("tr-TR")}</p>
                          <p className="text-[9px] font-medium text-gray-400 mt-0.5">Dağıtılmayan</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Action Buttons — sağa dayalı (mobilde alt satıra döker, yatay scroll) */}
          <div className="flex gap-2 shrink-0 sm:ml-auto overflow-x-auto no-scrollbar -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap">
            {!readOnly && hasPermission("firms.giveQuota") && (
              <button
                onClick={() => setQuotaModal({ open: true, type: "upload" })}
                disabled={quotaActionLoading}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${quotaActionLoading ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-emerald-400 text-white hover:bg-emerald-500 active:scale-[0.97]"}`}
              >
                {giveLoading ? (
                  <ThreeDots visible={true} height={12} width={18} color="#fff" />
                ) : (
                  <FaPlus className="w-3 h-3 shrink-0" />
                )}
                <span>Bedelsiz Kota Yükle</span>
              </button>
            )}
            {!readOnly && hasPermission("firms.takeBackQuota") && (
              <button
                onClick={() => setQuotaModal({ open: true, type: "return" })}
                disabled={quotaActionLoading}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${quotaActionLoading ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-amber-400 text-white hover:bg-amber-500 active:scale-[0.97]"}`}
              >
                {takebackLoading ? (
                  <ThreeDots visible={true} height={12} width={18} color="#fff" />
                ) : (
                  <FaMinus className="w-3 h-3 shrink-0" />
                )}
                <span>İade Kota Al</span>
              </button>
            )}
            {!readOnly && hasPermission("firms.distributeQuota") && (
              <button
                onClick={handleDistributeQuota}
                disabled={quotaActionLoading || distributeInfo.activeDevices === 0}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${isDistributingQuota || distributeInfo.activeDevices === 0 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-indigo-500 text-white hover:bg-indigo-600 active:scale-[0.97]"}`}
              >
                {isDistributingQuota ? (
                  <ThreeDots visible={true} height={12} width={18} color="#fff" />
                ) : (
                  <FaRandom className="w-3 h-3 shrink-0" />
                )}
                <span>Kota Dağıt</span>
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={deviceIsLoading || deviceIsFetching}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap disabled:opacity-40 ${deviceIsLoading || deviceIsFetching ? "bg-gray-200 text-gray-500" : "bg-slate-500 text-white hover:bg-slate-600 active:scale-[0.97]"}`}
            >
              <FaSync className={`w-3 h-3 shrink-0 ${deviceIsLoading || deviceIsFetching ? "animate-spin" : ""}`} />
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>

      {/* İçerik — tablo görünümü kenar boşluksuz, sabit başlıkla kendi
          içinde kayar; loading/empty durumları eski padding'li düzende */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Hata Durumu */}
        {deviceIsError && (
          <div className="mx-2 sm:mx-4 mt-3 mb-1 p-3 bg-red-50 border border-red-200 rounded-xl shrink-0">
            <div className="flex items-center gap-2">
              <FaExclamationCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-red-700 text-xs font-semibold">
                {deviceError?.data?.message || "Cihaz verileri alınamadı. Lütfen tekrar deneyin."}
              </span>
            </div>
          </div>
        )}

        {/* Ana İçerik */}
        {deviceIsLoading || deviceIsFetching ? (
          <div className="relative flex-1 min-h-0">
            <MascotLoadingOverlay message="Cihazlar yükleniyor..." />
            <DevicesTableSkeleton rows={6} />
          </div>
        ) : deviceIsError || devices.length === 0 || hasNoData ? (
          <div className="flex-1 overflow-y-auto min-h-0 px-2 sm:px-4 py-3">
            <EmptyState
              message={isSearching || filterSerialNo || filterEyeCount ? "Sonuç bulunamadı" : "Kayıtlı Cihaz Bulunamadı"}
              suggestion={isSearching || filterSerialNo || filterEyeCount ? "Filtreleri değiştirerek tekrar deneyin." : "Bu firmaya ait kayıtlı cihaz bulunmamaktadır."}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <DevicesTable
              devices={displayDevices}
              firm={firm}
              isDistributingQuota={isDistributingQuota}
              onOpenOperations={(device, view) =>
                setOperationsDrawer({ open: true, deviceId: device._id, view })
              }
              onOpenOperationDrawer={handleOpenOperationDrawer}
              onOpenManualQuota={handleOpenManualQuota}
              onOpenSettingsDrawer={handleOpenSettingsDrawer}
              onToggleQuotaDistribution={handleToggleQuotaDistribution}
              onDistributeQuota={
                !readOnly && hasPermission("firms.distributeQuota") && distributeInfo.activeDevices > 0
                  ? handleDistributeQuota
                  : undefined
              }
              hideDeviceOperationButtons={readOnly}
              onOpenCenterDealerMove={
                readOnly ? undefined : (device, mode) => setCenterDealerMove({ open: true, device, mode })
              }
              onOpenCenterDealerQuota={
                readOnly ? undefined : (device, direction) => setCenterDealerQuota({ open: true, device, direction })
              }
            />
          </div>
        )}

      </div>{/* end scrollable content */}

      {/* Operasyonlar Drawer — tablo satırından açılır */}
      <DeviceOperationsDrawer
        open={operationsDrawer.open}
        onClose={() => setOperationsDrawer({ open: false, deviceId: null, view: null })}
        device={operationsDrawerDevice}
        initialView={operationsDrawer.view}
        isProcessing={isDistributingQuota}
        isDistributingQuota={isDistributingQuota}
        onToggleQuotaDistribution={handleToggleQuotaDistribution}
        onOpenOperationDrawer={handleOpenOperationDrawer}
        onOpenManualQuota={handleOpenManualQuota}
        onOpenSettingsDrawer={handleOpenSettingsDrawer}
        hideDeviceOperationButtons={readOnly}
      />

      {!readOnly && (
        <CenterDealerMoveDrawer
          open={centerDealerMove.open}
          device={centerDealerMove.device}
          mode={centerDealerMove.mode}
          onClose={() => setCenterDealerMove((prev) => ({ ...prev, open: false }))}
          onDone={() => refreshAllData()}
        />
      )}
      {!readOnly && (
        <CenterDealerQuotaDrawer
          open={centerDealerQuota.open}
          device={centerDealerQuota.device}
          direction={centerDealerQuota.direction}
          onClose={() => setCenterDealerQuota((prev) => ({ ...prev, open: false }))}
          onDone={() => refreshAllData()}
        />
      )}

      {/* İşlem Drawer */}
      <OperationDrawer
        open={drawerOpen}
        device={drawerDevice}
        operation={drawerOperation}
        onClose={handleDrawerClose}
        onConfirm={handleDrawerConfirm}
        playmakers={playmakerData?.data?.playmakers || []}
        isLoading={serviceMoveStatus.isLoading || resultToggleDeviceActivity.isLoading || playmakerMoveStatus.isLoading}
        isSuccess={
          (drawerOperation === "Servis" && serviceMoveStatus.isSuccess) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && resultToggleDeviceActivity.isSuccess) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isSuccess)
        }
        isError={
          (drawerOperation === "Servis" && serviceMoveStatus.isError) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && resultToggleDeviceActivity.isError) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isError)
        }
        errorMessage={
          serviceMoveStatus.error?.data?.message ||
          resultToggleDeviceActivity.error?.data?.message ||
          playmakerMoveStatus.error?.data?.message ||
          ""
        }
      />

      {/* Bedelsiz Kota Yükle / İade Kota Al — Drawer (open ile kayma animasyonu çalışsın diye hep render) */}
      <QuotaOperationDrawer
        open={quotaModal.open}
        onClose={() => setQuotaModal({ open: false, type: "" })}
        onSubmit={handleQuotaSubmit}
        type={quotaModal.type}
        products={productDatas}
        productInfoes={(syncDataFromResponse?.productInfoes ?? syncDataResponse?.data?.productInfoes)?.filter(
          (p) => p.quotaDistribution !== false,
        )}
        quotaArray={syncDataFromResponse?.quotaArray ?? syncDataResponse?.data?.quotaArray}
        devices={devices}
        isLoading={giveLoading || takebackLoading}
      />

      <ManualQuotaDrawer
        open={manualQuotaDrawer.open}
        onClose={() => setManualQuotaDrawer({ open: false, device: null })}
        device={manualQuotaDrawer.device}
        onSuccess={handleManualQuotaSuccess}
      />

      <DeviceSettingsDrawer
        open={settingsDrawer.open}
        onClose={() => setSettingsDrawer({ open: false, device: null })}
        device={settingsDrawer.device}
      />

      {/* Kota Dağıtımı Toggle Modal */}
      <Modal
        open={showQuotaToggleModal}
        onClose={() => setShowQuotaToggleModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            style: { backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" },
          },
        }}
        style={{ zIndex: 10001 }}
      >
        <Fade in={showQuotaToggleModal}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "90%", maxWidth: 420, bgcolor: "background.paper", borderRadius: "16px", boxShadow: 24, p: 0, outline: "none",
          }}>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className={`flex items-center justify-between p-4 border-b ${quotaToggleProduct?.quotaDistribution ? "bg-red-50" : "bg-emerald-50"
                }`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 text-lg rounded-full ${quotaToggleProduct?.quotaDistribution
                      ? "bg-red-100 text-red-600"
                      : "bg-emerald-100 text-emerald-600"
                    }`}>
                    <FaCog />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Kota Dağıtım Ayarı
                  </h3>
                </div>
                <button onClick={() => setShowQuotaToggleModal(false)} className="p-2 rounded-full text-xl hover:bg-gray-200">
                  ×
                </button>
              </div>

              <div className="p-5 space-y-4">
                {quotaToggleDevice && quotaToggleProduct && (
                  <>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-500">Cihaz</p>
                      <p className="text-sm font-bold text-gray-800">{quotaToggleDevice.customName || quotaToggleDevice.deviceName}</p>
                      <p className="text-xs text-gray-400 font-mono">{quotaToggleDevice.serialNo}</p>
                    </div>

                    <div className={`rounded-xl p-4 border-2 ${quotaToggleProduct.quotaDistribution
                        ? "border-red-200 bg-red-50"
                        : "border-emerald-200 bg-emerald-50"
                      }`}>
                      <p className="text-sm font-semibold text-gray-800 mb-1">
                        {quotaToggleProduct.productName}
                      </p>
                      <p className="text-xs text-gray-600">
                        {quotaToggleProduct.quotaDistribution
                          ? "Bu cihazın bu ürününe kota dağıtımı kapatılacaktır. Artık kota verilmeyecek."
                          : "Bu cihazın bu ürününe kota dağıtımı açılacaktır. Kota verilecek."
                        }
                      </p>
                      {quotaToggleProduct.quotaDistribution && (
                        <p className="text-xs text-gray-600 mt-1.5">
                          Bu üründeki kota <span className="font-semibold text-red-600">firmanın kotasına iade edilecek</span> ve bu kotayı <span className="font-semibold text-red-600">farklı makineler çekebilecektir</span>.
                        </p>
                      )}
                      {/* Kapatma senaryosu: iade edilecek cihaz kotası ve
                          firmanın dağıtılmamış kotasının alacağı yeni değer */}
                      {quotaToggleProduct.quotaDistribution && (() => {
                        const deviceQuota =
                          quotaToggleDevice.productInfo?.productInfo?.find(
                            (p) => p.productName === quotaToggleProduct.productName
                          )?.quota ?? 0;
                        const firmProduct = productsForCards.find(
                          (p) => p.productName === quotaToggleProduct.productName
                        );
                        const firmUndistributed = firmProduct?.undistributedQuota ?? 0;
                        return (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <div className="bg-white rounded-lg p-2.5 text-center border border-red-100">
                              <p className="text-lg font-Bold text-red-600 tabular-nums">
                                {deviceQuota.toLocaleString("tr-TR")}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">Cihazdan İade Edilecek</p>
                            </div>
                            <div className="bg-white rounded-lg p-2.5 text-center border border-emerald-100">
                              <p className="text-lg font-Bold text-emerald-600 tabular-nums whitespace-nowrap">
                                {firmUndistributed.toLocaleString("tr-TR")}
                                <span className="mx-1 text-gray-400 font-normal">→</span>
                                {(firmUndistributed + deviceQuota).toLocaleString("tr-TR")}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">Firma Dağıtılmamış Kota</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>

              <div className="px-5 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowQuotaToggleModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handleQuotaToggleConfirm}
                  disabled={updateDeviceSettingResult.isLoading}
                  className={`flex-1 px-4 py-2.5 text-white rounded-xl font-semibold transition-colors text-sm disabled:bg-gray-300 disabled:cursor-not-allowed ${quotaToggleProduct?.quotaDistribution
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-emerald-500 hover:bg-emerald-600"
                    }`}
                >
                  {updateDeviceSettingResult.isLoading
                    ? "İşleniyor..."
                    : quotaToggleProduct?.quotaDistribution
                      ? "Kapat"
                      : "Aç"
                  }
                </button>
              </div>
            </div>
          </Box>
        </Fade>
      </Modal>

      {/* Kota Dağıt Onay Modal */}
      <Modal
        open={showDistributeConfirm}
        onClose={() => setShowDistributeConfirm(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            style: { backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" },
          },
        }}
        style={{ zIndex: 10001 }}
      >
        <Fade in={showDistributeConfirm}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "90%", maxWidth: 480, bgcolor: "background.paper", borderRadius: "16px", boxShadow: 24, p: 0, outline: "none",
          }}>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b bg-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 text-lg rounded-full bg-indigo-100 text-indigo-600">
                    <FaRandom />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Kota Dağıtımı</h3>
                </div>
                <button onClick={() => setShowDistributeConfirm(false)} className="p-2 rounded-full text-xl hover:bg-gray-200">×</button>
              </div>

              <div className="p-5 space-y-4">
                {/* Cihaz Özeti */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-gray-800">{distributeInfo.totalDevices}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Toplam Cihaz</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{distributeInfo.activeDevices}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Kota Almaya Açık Cihaz</p>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-700">{distributeInfo.quotaEnabledDevices}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Dağıtıma Açık</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{distributeInfo.totalDeviceQuota.toLocaleString("tr-TR")}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Cihazlardaki Kota</p>
                  </div>
                </div>

                {/* Kapsamdaki firmalar (#386) — birden fazlaysa */}
                {distributeInfo.scopeFirms.length > 1 && (
                  <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600 tracking-wide">
                      {distributeInfo.scopeFirms.length} FİRMADA DAĞITILACAK
                    </p>
                    <div className="max-h-28 overflow-y-auto flex flex-wrap gap-1.5">
                      {distributeInfo.scopeFirms.map((f) => (
                        <span key={f._id} className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-xs text-gray-700">
                          {f.firmName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ürün Bazlı Dağıtılmamış Kota — yalnız kök firmanın havuzu (bu sayfada yüklü olan) */}
                {rootInScope && productsForCards?.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ürün Bazlı Dağıtılmamış Kota</p>
                    {productsForCards.map((p) => (
                      <div key={p._id} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{p.productName}</span>
                        <span className={`text-sm font-bold ${p.undistributedQuota < 0 ? "text-red-500" : p.undistributedQuota > 0 ? "text-indigo-600" : "text-gray-400"}`}>
                          {p.undistributedQuota.toLocaleString("tr-TR")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bilgi notu */}
                <div className="flex items-start gap-2 bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <FaInfoCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    {distributeInfo.scopeFirms.length > 1
                      ? "Her firmanın dağıtılmamış kotası, o firmanın kota almaya açık ve kullanıma açık cihazlarına eşit şekilde dağıtılacaktır. Ana firmanın kotası alt firmalara geçmez."
                      : "Dağıtılmamış kotalar, kota almaya açık olan kullanıma açık cihazlara eşit şekilde dağıtılacaktır."}
                  </p>
                </div>
              </div>

              <div className="px-5 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowDistributeConfirm(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handleDistributeConfirm}
                  className="flex-1 px-4 py-2.5 bg-indigo-500 text-white rounded-xl font-semibold hover:bg-indigo-600 transition-colors text-sm"
                >
                  Kota Dağıt
                </button>
              </div>
            </div>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default DevicesTab;
